import { useEffect, useState } from "react";
import { db } from "../../../firebase/firestore";
import {
  addDoc,
  collection,
  onSnapshot,
  updateDoc,
  doc,
  increment,
  writeBatch,
} from "firebase/firestore";
import jsPDF from "jspdf";

function toMillis(value) {
  if (!value) return 0;
  if (typeof value?.toDate === "function") {
    return value.toDate().getTime();
  }
  if (Number.isFinite(value?.seconds)) {
    return Number(value.seconds) * 1000;
  }
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function getOrderMillis(order) {
  return toMillis(order?.createdAt) || Number(order?.createdAtMs || 0) || 0;
}

function getOrderStatus(order) {
  return String(order?.status || "pending").toLowerCase();
}

function formatOrderDate(order) {
  const ms = getOrderMillis(order);
  if (!ms) return "N/A";
  return new Date(ms).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getOrderTotal(order) {
  return Number(order?.total ?? order?.payableTotal ?? order?.pricing?.total ?? 0) || 0;
}

function getOrderCustomerName(order) {
  return String(order?.name || order?.customer?.name || "User").trim() || "User";
}

function formatOrderAddress(order) {
  const raw = order?.address;
  if (typeof raw === "string" && raw.trim()) return raw;

  if (raw && typeof raw === "object") {
    const parts = [
      raw.flatNo,
      raw.buildingNumber,
      raw.road,
      raw.landmark ? `Near ${raw.landmark}` : "",
      raw.pinCode,
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean);

    if (parts.length) return parts.join(", ");
  }

  return order?.addressText || "N/A";
}

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filter, setFilter] = useState("all");

  // 🔥 REAL-TIME FETCH
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "orders"), (snap) => {
      const data = snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setOrders(data);
    });

    return () => unsubscribe();
  }, []);

  // 🔥 WHATSAPP ALERT
  const sendWhatsApp = (order, message) => {
    if (!order.phone) return;

    const url = `https://wa.me/91${order.phone}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  const adjustInventoryForDeliveredOrder = async (orderId, order) => {
    if (order.inventoryAdjustedAt) return;

    const batch = writeBatch(db);
    let hasStockAdjustments = false;

    (order.items || []).forEach((item) => {
      const itemId = item.id;
      if (!itemId) return;

      const qty = Number(item.qty || 0);
      const weight = Number(item.weight || item.size || 0);
      const usedGrams = qty * weight;
      if (!usedGrams) return;

      hasStockAdjustments = true;
      batch.update(doc(db, "items", itemId), {
        stockGrams: increment(-usedGrams),
      });
    });

    batch.update(doc(db, "orders", orderId), {
      inventoryAdjustedAt: new Date().toISOString(),
    });

    if (hasStockAdjustments) {
      await batch.commit();
      return;
    }

    await updateDoc(doc(db, "orders", orderId), {
      inventoryAdjustedAt: new Date().toISOString(),
    });
  };

  // 🔥 STATUS UPDATE (LOCK SYSTEM)
  const updateStatus = async (id, status, order) => {

    // ❌ BLOCK IF CANCELLED
    if (getOrderStatus(order) === "cancelled") {
      alert("Order already cancelled ❌");
      return;
    }

    // 🔒 BLOCK IF DELIVERED
    if (getOrderStatus(order) === "delivered") {
      alert("Order already delivered ✅");
      return;
    }

    await updateDoc(doc(db, "orders", id), { status });

    if (String(status).toLowerCase() === "delivered") {
      await adjustInventoryForDeliveredOrder(id, order);
    }

    if (order.userId) {
      await addDoc(collection(db, "notifications"), {
        userId: order.userId,
        title: "Order status updated",
        message: `Your order ${order.id || id} is now ${status}.`,
        read: false,
        orderId: order.id || id,
        createdAt: new Date().toISOString(),
        createdAtMs: Date.now(),
      });
    }

    // 🔥 WHATSAPP MESSAGE
    sendWhatsApp(order, `Your order is now ${status} 🍽`);
  };

  // 🚚 DELIVERY TOMORROW
  const setDeliveryTomorrow = async (id, order) => {

    if (getOrderStatus(order) === "cancelled") return;

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    await updateDoc(doc(db, "orders", id), {
      deliveryDate: tomorrow.toISOString(),
    });

    if (order.userId) {
      await addDoc(collection(db, "notifications"), {
        userId: order.userId,
        title: "Delivery schedule updated",
        message: `Your order ${order.id || id} is scheduled for tomorrow.`,
        read: false,
        orderId: order.id || id,
        createdAt: new Date().toISOString(),
        createdAtMs: Date.now(),
      });
    }

    sendWhatsApp(order, "Your order will be delivered tomorrow 🚚");
  };

  // ❌ CANCEL ORDER (FINAL LOCK)
  const cancelOrder = async (id, order) => {

    if (getOrderStatus(order) === "cancelled") {
      alert("Already cancelled");
      return;
    }

    await updateDoc(doc(db, "orders", id), {
      status: "cancelled",
      cancelledAt: new Date().toISOString()
    });

    if (order.userId) {
      await addDoc(collection(db, "notifications"), {
        userId: order.userId,
        title: "Order cancelled",
        message: `Your order ${order.id || id} has been cancelled.`,
        read: false,
        orderId: order.id || id,
        createdAt: new Date().toISOString(),
        createdAtMs: Date.now(),
      });
    }

    // 🔥 WHATSAPP ALERT
    sendWhatsApp(order, "Your order has been cancelled ❌");
  };

  // 📊 FILTER (IGNORE CANCELLED FROM MAIN VIEW)
  const filteredOrders = orders
    .filter((o) => {
      if (filter === "all") return true;
      if (filter === "pending") return getOrderStatus(o) === "pending";
      if (filter === "delivered") return getOrderStatus(o) === "delivered";
      if (filter === "cancelled") return getOrderStatus(o) === "cancelled";
      return true;
    })
    .sort((a, b) => getOrderMillis(b) - getOrderMillis(a));

  // 🧾 PDF
  const generateInvoice = (order) => {
    const docPdf = new jsPDF();
    const displayAddress = formatOrderAddress(order);

    docPdf.setFontSize(18);
    docPdf.text("Shreeji Surti Swad", 20, 20);

    docPdf.setFontSize(12);
    docPdf.text(`Order ID: ${order.id}`, 20, 30);
    docPdf.text(`Customer: ${order.name || order.customer?.name || "User"}`, 20, 40);
    docPdf.text(`Phone: ${order.phone || order.customer?.phone || "N/A"}`, 20, 50);
    docPdf.text(`Address: ${displayAddress}`, 20, 60);

    let y = 80;

    order.items?.forEach((item, index) => {
      y += 10;
      docPdf.text(
        `${index + 1}. ${item.name} x${item.qty} = ₹${item.price}`,
        20,
        y
      );
    });

    y += 20;
    docPdf.text(`Total: ₹${order.total ?? order.payableTotal ?? order.pricing?.total ?? 0}`, 20, y);

    docPdf.save(`invoice_${order.id}.pdf`);
  };

  return (
    <div className="orders-container">

      <h1>📦 Orders</h1>

      {/* 🔥 FILTER */}
      <div className="filters">
        <button onClick={() => setFilter("all")}>All</button>
        <button onClick={() => setFilter("pending")}>Pending</button>
        <button onClick={() => setFilter("delivered")}>Delivered</button>
        <button onClick={() => setFilter("cancelled")}>Cancelled</button>
      </div>

      {/* 🔥 LIST */}
      {!filteredOrders.length ? (
        <div className="card orders-empty">No orders found for selected filter.</div>
      ) : filteredOrders.map((o) => (
        <div key={o.id} className={`order-card ${getOrderStatus(o)}`}>

          <div className="order-top">
            <h3>👤 {getOrderCustomerName(o)}</h3>
            <span className="price">₹{getOrderTotal(o)}</span>
          </div>

          <div className="order-meta">
            <span className={`status-chip ${getOrderStatus(o)}`}>{getOrderStatus(o)}</span>
            <span className="order-date">{formatOrderDate(o)}</span>
          </div>

          <p>Items: <b>{Array.isArray(o.items) ? o.items.length : 0}</b></p>

          {/* 🔥 LOCK SELECT */}
          <select
            value={getOrderStatus(o)}
            disabled={getOrderStatus(o) === "cancelled" || getOrderStatus(o) === "delivered"}
            onChange={(e) => updateStatus(o.id, e.target.value, o)}
          >
            <option value="pending">Pending</option>
            <option value="preparing">Preparing</option>
            <option value="out">Out</option>
            <option value="delivered">Delivered</option>
          </select>

          {/* 🔥 ACTIONS */}
          <div className="actions">

            <button onClick={() => setSelectedOrder(o)}>
              👁 View
            </button>

            <button onClick={() => setDeliveryTomorrow(o.id, o)}>
              🚚 Tomorrow
            </button>

            <button onClick={() => generateInvoice(o)}>
              🧾 Invoice
            </button>

            <button
              className="cancel"
              disabled={getOrderStatus(o) === "cancelled"}
              onClick={() => cancelOrder(o.id, o)}
            >
              ❌ Cancel
            </button>

          </div>

        </div>
      ))}

      {/* 🔥 POPUP */}
      {selectedOrder && (
        <div className="modal">
          <div className="modal-box">

            <h2>Order Details</h2>

            <p><b>Name:</b> {selectedOrder.name}</p>
            <p><b>Phone:</b> {selectedOrder.phone || selectedOrder.customer?.phone || "N/A"}</p>
            <p><b>Address:</b> {formatOrderAddress(selectedOrder)}</p>

            <h3>Items:</h3>

            {selectedOrder.items?.map((item, i) => (
              <div key={i} className="item-row">
                <span>{item.name}</span>
                <span>x{item.qty}</span>
                <span>₹{item.price}</span>
              </div>
            ))}

            <h3>Total: ₹{selectedOrder.total ?? selectedOrder.payableTotal ?? selectedOrder.pricing?.total ?? 0}</h3>

            <button onClick={() => setSelectedOrder(null)}>
              Close
            </button>

          </div>
        </div>
      )}

    </div>
  );
}